import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import './OrderHistory.css';

const orderHistoryTranslations = {
  en: {
    title: "Order History",
    noOrders: "You haven't placed any orders yet.",
    orderId: "Order ID",
    date: "Date",
    status: "Status",
    items: "Items",
    total: "Total",
    shopNow: "Shop Now",
    pending: "Pending",
    dispatch: "Dispatched",
    completed: "Completed",
    qty: "Qty",
    price: "Price",
    shippingMethod: "Shipping Method",
    loading: "Loading Order History..."
  },
  nl: {
    title: "Bestelgeschiedenis",
    noOrders: "U heeft nog geen bestellingen geplaatst.",
    orderId: "Bestel-ID",
    date: "Datum",
    status: "Status",
    items: "Artikelen",
    total: "Totaal",
    shopNow: "Nu Winkelen",
    pending: "In Afwachting",
    dispatch: "Verzonden",
    completed: "Voltooid",
    qty: "Aantal",
    price: "Prijs",
    shippingMethod: "Verzendmethode",
    loading: "Bestelgeschiedenis laden..."
  },
  es: {
    title: "Historial de Pedidos",
    noOrders: "Aún no has realizado ningún pedido.",
    orderId: "ID del Pedido",
    date: "Fecha",
    status: "Estado",
    items: "Artículos",
    total: "Total",
    shopNow: "Comprar Ahora",
    pending: "Pendiente",
    dispatch: "Enviado",
    completed: "Completado",
    qty: "Cant.",
    price: "Precio",
    shippingMethod: "Método de envío",
    loading: "Cargando historial de pedidos..."
  },
  fr: {
    title: "Historique des Commandes",
    noOrders: "Vous n'avez pas encore passé de commande.",
    orderId: "ID de Commande",
    date: "Date",
    status: "Statut",
    items: "Articles",
    total: "Total",
    shopNow: "Acheter Maintenant",
    pending: "En attente",
    dispatch: "Expédié",
    completed: "Terminé",
    qty: "Qté",
    price: "Prix",
    shippingMethod: "Mode de livraison",
    loading: "Chargement de l'historique des commandes..."
  },
  de: {
    title: "Bestellverlauf",
    noOrders: "Sie haben noch keine Bestellungen aufgegeben.",
    orderId: "Bestell-ID",
    date: "Datum",
    status: "Status",
    items: "Artikel",
    total: "Gesamt",
    shopNow: "Jetzt Einkaufen",
    pending: "Ausstehend",
    dispatch: "Versandt",
    completed: "Abgeschlossen",
    qty: "Menge",
    price: "Preis",
    shippingMethod: "Versandart",
    loading: "Bestellverlauf wird geladen..."
  },
  it: {
    title: "Cronologia Ordini",
    noOrders: "Non hai ancora effettuato ordini.",
    orderId: "ID Ordine",
    date: "Data",
    status: "Stato",
    items: "Articoli",
    total: "Totale",
    shopNow: "Acquista Ora",
    pending: "In attesa",
    dispatch: "Spedito",
    completed: "Completato",
    qty: "Qtà",
    price: "Prezzo",
    shippingMethod: "Metodo di spedizione",
    loading: "Caricamento cronologia ordini..."
  },
  pt: {
    title: "Histórico de Pedidos",
    noOrders: "Ainda não efetuou nenhuma encomenda.",
    orderId: "ID da Encomenda",
    date: "Data",
    status: "Estado",
    items: "Itens",
    total: "Total",
    shopNow: "Comprar Agora",
    pending: "Pendente",
    dispatch: "Enviado",
    completed: "Concluído",
    qty: "Qtd",
    price: "Preço",
    shippingMethod: "Método de envio",
    loading: "A carregar histórico de encomendas..."
  },
  el: {
    title: "Ιστορικό Παραγγελιών",
    noOrders: "Δεν έχετε κάνει καμία παραγγελία ακόμα.",
    orderId: "ID Παραγγελίας",
    date: "Ημερομηνία",
    status: "Κατάσταση",
    items: "Προϊόντα",
    total: "Σύνολο",
    shopNow: "Αγορά Τώρα",
    pending: "Εκκρεμεί",
    dispatch: "Απεστάλη",
    completed: "Ολοκληρώθηκε",
    qty: "Ποσ.",
    price: "Τιμή",
    shippingMethod: "Μέθοδος Αποστολής",
    loading: "Φόρτωση ιστορικού παραγγελιών..."
  }
};

const OrderHistory = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = orderHistoryTranslations[selectedLang.code || 'en'] || orderHistoryTranslations.en;
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const session = localStorage.getItem('user_session');
    if (!session) {
      window.history.pushState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    
    const parsedUser = JSON.parse(session);
    setUser(parsedUser);

    const fetchOrders = async () => {
      try {
        const response = await fetch(`/api/orders/email/${encodeURIComponent(parsedUser.email)}`);
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        }
      } catch (error) {
        console.error("Failed to fetch order history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Completed': return 'badge-status-completed';
      case 'Dispatch': return 'badge-status-dispatch';
      default: return 'badge-status-pending';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Completed': return t.completed;
      case 'Dispatch': return t.dispatch;
      default: return t.pending;
    }
  };

  const handleContinueShopping = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (loading) {
    return (
      <div className="order-history-loading">
        <div className="modern-loading-screen">
          <div className="loader-orbit">
            <div className="orbit-item"></div>
            <div className="orbit-item"></div>
          </div>
          <span className="loader-text">{t.loading}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history-wrapper">
      <div className="order-history-container">
        <h1 className="history-page-title">{t.title}</h1>
        
        {orders.length === 0 ? (
          <div className="empty-history-box animate-fade-in">
            <div className="empty-icon-circle">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4l3 3" />
              </svg>
            </div>
            <p className="empty-text">{t.noOrders}</p>
            <button className="btn-shop-history" onClick={handleContinueShopping}>{t.shopNow}</button>
          </div>
        ) : (
          <div className="orders-cards-list">
            {orders.map((order, idx) => (
              <div key={order._id || idx} className="order-card-item animate-fade-in" style={{ animationDelay: `${idx * 0.05}s` }}>
                <div className="order-card-header">
                  <div className="header-meta-group">
                    <span className="order-meta-label">{t.orderId}:</span>
                    <span className="order-meta-id">#{order._id.substring(order._id.length - 6).toUpperCase()}</span>
                  </div>

                  {order.shippingMethod && (
                    <div className="header-meta-group" style={{ marginLeft: '1.5rem' }}>
                      <span className="order-meta-label">{t.shippingMethod}:</span>
                      <span className="order-meta-method" style={{ fontWeight: 600, fontSize: '0.85rem' }}>{order.shippingMethod}</span>
                    </div>
                  )}

                  <div className="header-meta-group text-right">
                    <span className="order-meta-label">{t.date}:</span>
                    <span className="order-meta-date">{new Date(order.createdAt).toLocaleDateString(selectedLang.code, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>

                <div className="order-card-body">
                  <div className="order-items-grid">
                    {order.items && order.items.map((item, i) => (
                      <div key={i} className="order-product-row">
                        <div className="product-row-left">
                          <span className="product-item-name">{item.name}</span>
                          {item.deliveryTime && (
                            <span className="product-item-delivery" style={{ display: 'block', fontSize: '0.75rem', color: '#718096', marginTop: '3px' }}>
                              🕒 Delivery: {item.deliveryTime}
                            </span>
                          )}
                        </div>
                        <div className="product-row-right">
                          <span className="product-item-qty">{t.qty}: {item.quantity}</span>
                          <span className="product-item-price">€{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="order-card-footer">
                  <span className={`status-badge-history ${getStatusBadgeClass(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                  
                  <div className="order-total-group">
                    <span className="total-label">{t.total}:</span>
                    <span className="total-value">€{order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
